import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";
import {Box, IconButton, TextField, Typography} from "@mui/material";
import {Delete, Edit} from "@mui/icons-material";
import {SubmitButton, CancelButton} from "../buttons";
import {SkeletonDialog} from "./SkeletonDialog";
import StyledDialog from "./StyledDialog";
import {ALERT} from "../../resources/environment";
import {useOrgPageStyles} from "../../pages/Organization/OrganizationStyles";
import {filter, map} from "underscore";

const MAX_MEMORY_CONTENT_LENGTH = 150;

const EntryContent = ({entry, editingId, editContent, setEditContent, confirmDeleteId}) => {
  if (editingId === entry?.memoryId) {
    const atLimit = editContent.length >= MAX_MEMORY_CONTENT_LENGTH;
    return (
      <Box flex={1} mr={1}>
        <TextField
          value={editContent}
          onChange={(e) => setEditContent(e.target.value)}
          inputProps={{maxLength: MAX_MEMORY_CONTENT_LENGTH}}
          autoFocus
          size="small"
          variant="standard"
          fullWidth
          error={atLimit}
          helperText={(
            <Typography
              component="span"
              variant="caption"
              color={atLimit ? "error" : "text.secondary"}
            >
              {editContent.length}
              /
              {MAX_MEMORY_CONTENT_LENGTH}
            </Typography>
          )}
        />
      </Box>
    );
  }
  if (confirmDeleteId === entry?.memoryId) {
    return (
      <Typography flex={1} color="error">
        Remove this memory entry?
      </Typography>
    );
  }
  return <Typography flex={1}>{entry?.content}</Typography>;
};

EntryContent.propTypes = {
  entry: PropTypes.object,
  editingId: PropTypes.string,
  editContent: PropTypes.string,
  setEditContent: PropTypes.func,
  confirmDeleteId: PropTypes.string,
};

const EntryActions = ({
  entry,
  editingId,
  confirmDeleteId,
  onSave,
  onDelete,
  onCancel,
  onEditClick,
  onDeleteClick,
}) => {
  const isActive = editingId === entry?.memoryId || confirmDeleteId === entry?.memoryId;

  if (isActive) {
    return (
      <Box display="flex" gap={1}>
        <SubmitButton
          onSubmit={() =>
            editingId === entry?.memoryId ? onSave(entry?.memoryId) : onDelete(entry?.memoryId)}
        >
          {editingId === entry?.memoryId ? "Save" : "Remove"}
        </SubmitButton>
        <CancelButton variant="modal" onCancel={onCancel}>
          Cancel
        </CancelButton>
      </Box>
    );
  }

  return (
    <>
      <IconButton
        size="small"
        onClick={() => onEditClick(entry)}
        disabled={!!editingId || !!confirmDeleteId}
      >
        <Edit fontSize="small" />
      </IconButton>
      <IconButton
        size="small"
        onClick={() => onDeleteClick(entry?.memoryId)}
        disabled={!!editingId || !!confirmDeleteId}
      >
        <Delete fontSize="small" />
      </IconButton>
    </>
  );
};

EntryActions.propTypes = {
  entry: PropTypes.object,
  editingId: PropTypes.string,
  confirmDeleteId: PropTypes.string,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  onCancel: PropTypes.func,
  onEditClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
};

const MemoryDialog = ({
  isOpen,
  onClose,
  title,
  emptyText,
  fetchEntries,
  updateEntry,
  deleteEntry,
  headerContent,
}) => {
  const [entries, setEntries] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editContent, setEditContent] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const {classes} = useOrgPageStyles();

  const loadEntries = async () => {
    try {
      const data = await fetchEntries();
      setEntries(data.entries || []);
    } catch (error) {
      ALERT.error("Failed to fetch memory entries.");
      console.error("Failed to fetch memory entries:", error);
      setEntries([]);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadEntries();
    }
  }, [isOpen]);

  const handleSave = async (memoryId) => {
    const cleaned = editContent.trim();
    if (!cleaned) {
      return;
    }
    try {
      await updateEntry(memoryId, cleaned);
      setEntries((prev) =>
        map(prev, (entry) => (entry?.memoryId === memoryId ? {...entry, content: cleaned} : entry)),
      );
      setEditingId(null);
      setEditContent("");
    } catch (error) {
      ALERT.error("Failed to update entry.");
      console.error("Failed to update memory entry:", error);
    }
  };

  const handleDelete = async (memoryId) => {
    try {
      await deleteEntry(memoryId);
      setEntries((prev) => filter(prev, (entry) => entry?.memoryId !== memoryId));
      setConfirmDeleteId(null);
    } catch (error) {
      ALERT.error("Failed to delete entry.");
      console.error("Failed to delete memory entry:", error);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setConfirmDeleteId(null);
  };

  const handleEditClick = (entry) => {
    setEditingId(entry?.memoryId);
    setEditContent(entry?.content);
  };

  const content = !entries ?
    (
      <SkeletonDialog numberOfItems={4} />
    ) :
    entries.length === 0 ?
      (
        <Typography color="text.secondary">{emptyText}</Typography>
      ) :
      (
        map(entries, (entry) => (
          <Box key={entry?.memoryId} className={classes.memoryEntry}>
            <EntryContent
              entry={entry}
              editingId={editingId}
              editContent={editContent}
              setEditContent={setEditContent}
              confirmDeleteId={confirmDeleteId}
            />
            <Box display="flex">
              <EntryActions
                entry={entry}
                editingId={editingId}
                confirmDeleteId={confirmDeleteId}
                onSave={handleSave}
                onDelete={handleDelete}
                onCancel={handleCancel}
                onEditClick={handleEditClick}
                onDeleteClick={setConfirmDeleteId}
              />
            </Box>
          </Box>
        ))
      );

  return (
    <StyledDialog isOpen={isOpen} onClose={onClose} title={title}>
      {headerContent && <Box mb={2}>{headerContent}</Box>}
      {content}
    </StyledDialog>
  );
};

MemoryDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  emptyText: PropTypes.string.isRequired,
  fetchEntries: PropTypes.func.isRequired,
  updateEntry: PropTypes.func.isRequired,
  deleteEntry: PropTypes.func.isRequired,
  headerContent: PropTypes.node,
};

export default MemoryDialog;
